import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import View from '@play-co/timestep-core/lib/ui/View';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import buttons from 'src/lib/ui/config/buttons';
import { ImageScaleView } from '@play-co/timestep-core/ui';
import { FlexBoxView, getFlexNode } from '@play-co/timestep-core/flexbox';
import { AssetGroup } from '@play-co/timestep-core';
import { Feature } from 'src/replicant/analytics';
import i18n from 'src/lib/i18n/i18n';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import StateObserver, { now, user } from 'src/StateObserver';
import { buddiesMaxParty } from 'src/replicant/buddies/buddies.ruleset';
import {
  buddiesGetEvent,
  buddiesIsEventCompleted,
} from 'src/replicant/buddies/buddies.getters';
import {
  openBuddiesLineSharePicker,
  buddiesShareInviteOthers,
} from 'src/game/buddies/buddies.share';
import { isShareTargetPickerAvailable } from 'src/lib/sharing';
import { withLoading } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import TabbedView from 'src/game/components/shared/tabs/TabbedView';
import NotificationBadge from 'src/game/components/shared/NotificationBadge';
import { downscale } from 'src/conf/scale';
import { BuddiesTabFriends } from '../components/BuddiesTabFriends';
import { BuddiesTabRequests } from '../components/BuddiesTabRequests';

const tabButtonConfig = {
  image: 'assets/ui/buddies/popupInvite/tab_inactive.png',
  imagePressed: 'assets/ui/buddies/popupInvite/tab_active.png',
  imageDisabled: 'assets/ui/buddies/popupInvite/tab_active.png',
  sourceSlices: {
    horizontal: { left: 40, right: 40 },
    vertical: { top: 25, bottom: 15 },
  },
};

type CloseHandler = (ok: boolean) => void;

export default class PopupBuddiesInvite extends PopupBasicLazy {
  private base: ImageScaleView;
  private close: CloseHandler;
  private headerTitle: LangBitmapFontTextView;
  private headerSubtitle: LangBitmapFontTextView;
  private buttonRow: FlexBoxView;
  private btnLine: ButtonScaleView;
  private btnOthers: ButtonScaleView;
  private tabs: TabbedView;
  private tabFriends: BuddiesTabFriends;
  private tabRequests: BuddiesTabRequests;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      skipBackground: true,
      skipTitle: true,
      close: () => {
        opts.close(false);
      },
      tracking: {
        feature: Feature.BUDDIES._,
        $subFeature: Feature.BUDDIES.POPUP_INFO,
      },
      zIndex: 10000,
    });

    // This color is the same as backgroundDarker in uiConfig.popups, but less transparent
    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.9)',
    });

    this.close = opts.close;

    this.base = new ImageScaleView({
      superview: this.box,
      width: downscale(1022),
      height: downscale(1842),
      scaleMethod: 'contain',
      image: 'assets/ui/buddies/popupInvite/base.png',
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: this.box.style.height / 2,
    });

    this.buttonClose.updateOpts({
      x: this.box.style.width + 10,
      y: -140,
      zIndex: 1,
    });

    this.headerTitle = new LangBitmapFontTextView({
      superview: this.base,
      x: this.base.style.width / 2,
      y: 85,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      size: 60,
      font: 'Body',
      color: '#FFFFFF',
      width: 500,
      height: 100,
      wordWrap: true,
      localeText: () => i18n('match2.buddies.invitePopup.title'),
    });

    this.headerSubtitle = new LangBitmapFontTextView({
      superview: this.base,
      x: this.base.style.width / 2,
      y: 135,
      align: 'center',
      verticalAlign: 'center',
      centerOnOrigin: true,
      size: 26,
      font: 'Body',
      color: '#99FF88',
      width: 290,
      height: 40,
      wordWrap: true,
    });

    this.tabs = new TabbedView({
      tabsFillWidth: true,
      tabMargin: 10,
      tabHeight: downscale(117),
      tabs: [
        {
          label: () => i18n('match2.buddies.invitePopup.tabFriends'),
          tracking: {
            feature: Feature.BUDDIES._,
            $subFeature: Feature.BUDDIES.TAB_FRIENDS,
          },
          onSelect: () => this.selectTab('friends'),
          style: {
            width: downscale(432),
            height: downscale(117),
            fontSize: 35,
          },
          buttonConfig: tabButtonConfig,
        },
        {
          label: () => i18n('match2.buddies.invitePopup.tabRequests'),
          tracking: {
            feature: Feature.BUDDIES._,
            $subFeature: Feature.BUDDIES.TAB_REQUESTS,
          },
          onSelect: () => this.selectTab('requests'),
          style: {
            width: downscale(432),
            height: downscale(117),
            fontSize: 35,
          },
          buttonConfig: tabButtonConfig,
        },
      ],
      alignment: 'center',
      superview: this.base,
      rect: {
        x: 35,
        y: 233,
        width: 610,
        height: 780,
      },
    });

    const [, requestsTabButton] = this.tabs.getTabs();
    const requestsBadge = new NotificationBadge({
      superview: requestsTabButton.getView(),
      size: 40,
      x: requestsTabButton.getView().style.width - 50,
      y: -12,
      font: 'Body',
      fontSize: 22,
      textColor: 'white',
      style: 'red',
      visible: false,
    });

    const [friendsTabView, requestsTabView] = this.tabs.getViews();

    // Tab contents
    this.tabFriends = new BuddiesTabFriends({
      tab: friendsTabView,
      onRefresh: () => this.refreshAll(),
    });

    this.tabRequests = new BuddiesTabRequests({
      tab: requestsTabView,
      onRefresh: () => this.refreshAll(),
      requestsBadge,
    });

    const locale = StateObserver.getLocale();
    const btnWidth = downscale(443);
    const btnHeight = downscale(151);

    this.buttonRow = new FlexBoxView({
      superview: this.base,
      x: 0,
      y: 1075,
      width: this.base.style.width,
      height: btnHeight,
      flex: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
      },
    });

    this.btnLine = new ButtonScaleView({
      superview: this.buttonRow,
      ...buttons.secondary,
      width: btnWidth,
      height: btnHeight,
      icon: {
        image: `assets/ui/buddies/popupInvite/btn_line_${locale}.png`,
        width: downscale(365),
        height: downscale(102),
        offset: { x: 0, y: -5 },
      },
      tracking: {
        feature: Feature.BUDDIES._,
        $subFeature: Feature.BUDDIES.BUTTON_START,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.loading(async () => {
          await openBuddiesLineSharePicker();
        });
      },
    });

    this.btnOthers = new ButtonScaleView({
      superview: this.buttonRow,
      ...buttons.blue,
      width: btnWidth,
      height: btnHeight,
      icon: {
        image: `assets/ui/buddies/popupInvite/btn_others_${locale}.png`,
        width: downscale(362),
        height: downscale(102),
        offset: { x: 0, y: -5 },
      },
      tracking: {
        feature: Feature.BUDDIES._,
        $subFeature: Feature.BUDDIES.BUTTON_START,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        await this.loading(async () => {
          await buddiesShareInviteOthers();
        });
      },
    });

    // Inter-button spacing in unscaled flex layout pixels
    for (const child of this.buttonRow.getSubviews()) {
      getFlexNode(child).updateStyle({
        marginLeft: 5,
        marginRight: 5,
      });
    }
  }

  public getAssetsGroup() {
    return new AssetGroup(
      AssetGroup.constructURLs([
        'assets/ui/buddies/common',
        'assets/ui/buddies/popupInvite',
        'assets/ui/match2/common/icon_cross.png',
        'assets/ui/shared/icons/notification_red.png',
      ]),
    );
  }

  public async init(args: {}) {
    super.init({});

    // Guard: don't open if event is completed
    if (buddiesIsEventCompleted(user(), now())) {
      this.close(false);
      return;
    }

    // Auto-select Requests tab if there are pending received invites
    const event = buddiesGetEvent(user(), now());
    const hasReceivedInvites = Object.values(event?.invites ?? {}).some(
      (inv) => inv.status === 'inviteReceived',
    );
    this.selectTab(hasReceivedInvites ? 'requests' : 'friends');

    this.loading(async () => {
      // First pass: show friends/invites/party immediately
      await Promise.all([
        this.tabFriends.refresh(),
        this.tabRequests.refresh(),
      ]);
      // Async fetch suggestions
      await this.tabFriends.fetchSuggestions();
      // Second pass: include suggestions
      await this.tabFriends.refresh();
      this.updateSubtitle();
      this.updateInviteButtonVisibility();
    });
  }

  public async fadeIn() {
    super.fadeIn();
  }

  public async fadeOut() {
    super.fadeOut();
  }

  private selectTab(tab: 'friends' | 'requests') {
    this.tabs.setProps({ selectedTabIndex: tab === 'friends' ? 0 : 1 });
    this.updateInviteButtonVisibility();
  }

  private updateInviteButtonVisibility() {
    if (isShareTargetPickerAvailable()) {
      this.btnLine.show();
    } else {
      this.btnLine.hide();
    }
  }

  private async refreshAll() {
    await this.tabFriends.refresh();
    await this.tabRequests.refresh();
    this.updateSubtitle();
    this.updateInviteButtonVisibility();
  }

  private updateSubtitle() {
    const event = buddiesGetEvent(user(), now());
    const partyEntries = Object.entries(event?.party ?? {});
    const slotsRemaining = Math.max(
      0,
      buddiesMaxParty - 1 - partyEntries.length,
    );
    if (slotsRemaining > 0) {
      this.headerSubtitle.localeText = () =>
        i18n('match2.buddies.invitePopup.partyAvailable', {
          num: slotsRemaining,
        });
    } else {
      this.headerSubtitle.localeText = () =>
        i18n('match2.buddies.invitePopup.partyFull');
    }
  }

  private async loading(fn: () => Promise<void>) {
    await withLoading(fn, {
      feature: Feature.BUDDIES._,
      subFeature: Feature.BUDDIES._,
      superview: this.box,
    });
  }
}
